import type { ImportJobDto, MenuSummaryDto } from "../../shared/api-types";
import { importStage } from "./import/importState";

// "Acasă" says the one next thing to do, from the real state (admin review 2026-11-02): an import running, a draft
// to verify, a menu to publish, changes waiting, allergens with a deadline, or nothing (the menu is live).

export interface NextStep {
  title: string;
  body: string;
  action: string;
  /** In-app route, or the public menu when `external`. */
  to: string;
  external?: boolean;
  tone: "info" | "warning" | "success";
}

export function nextStep(input: {
  menus: MenuSummaryDto[];
  main: MenuSummaryDto | undefined;
  latestImport: ImportJobDto | undefined;
  allergenDeadlines: number;
  publicUrl: string;
}): NextStep | null {
  const { menus, main, latestImport: job, allergenDeadlines, publicUrl } = input;
  if (job && ["uploading", "queued", "processing"].includes(job.status)) {
    return {
      title: "Importul se procesează",
      body: "Citim meniul și pregătim o ciornă. Poți închide pagina; te anunțăm aici când e gata.",
      action: "Vezi progresul",
      to: "/app/imports",
      tone: "info",
    };
  }
  if (job && job.status === "ready") {
    const menu = menus.find((m) => m.id === job.menuId);
    const stage = importStage(job, menu);
    if (stage === "needsReview" || stage === "readyToPublish") {
      return {
        title: stage === "needsReview" ? "Verifică meniul importat" : "Meniul importat este gata de publicat",
        body: `„${menu?.name ?? "Meniu importat"}”: ${job.summary?.products ?? 0} produse în ciornă. Nimic nu este public până nu publici.`,
        action: "Verifică meniul",
        to: `/app/verifica/${job.id}`,
        tone: "warning",
      };
    }
  }
  if (!main) return null;
  if (!main.publishedVersionId) {
    return main.productCount > 0
      ? {
          title: "Publică meniul",
          body: `„${main.name}” are ${main.productCount} produse în ciornă. Clienții îl văd după ce îl publici.`,
          action: "Verifică și publică",
          to: `/app/products?menu=${main.id}&publish=1`,
          tone: "warning",
        }
      : {
          title: "Adaugă primele produse",
          body: `„${main.name}” este gol. Importă meniul existent sau adaugă produsele manual.`,
          action: "Adaugă produse",
          to: `/app/products?menu=${main.id}`,
          tone: "info",
        };
  }
  if (!main.publicVisible) {
    return {
      title: "Meniul este retras din public",
      body: `Clienții nu văd „${main.name}”. Publică-l din nou ca să reapară la același link și cod QR.`,
      action: "Publică din nou",
      to: `/app/products?menu=${main.id}&publish=1`,
      tone: "info",
    };
  }
  if ((main.pendingChanges ?? 0) > 0) {
    return {
      title: "Ai modificări nepublicate",
      body: `${main.pendingChanges} ${main.pendingChanges === 1 ? "modificare" : "modificări"} în „${main.name}”. Clienții văd încă versiunea publicată.`,
      action: "Revizuiește și publică",
      to: `/app/products?menu=${main.id}&publish=1`,
      tone: "warning",
    };
  }
  if (allergenDeadlines > 0) {
    return {
      title: "Confirmă alergenii",
      body: `${allergenDeadlines} ${allergenDeadlines === 1 ? "produs public are" : "produse publice au"} alergenii neconfirmați. Clienții văd estimările Meniova până le confirmi.`,
      action: "Rezolvă",
      to: "/app/nutrition/alergeni",
      tone: "warning",
    };
  }
  const otherDraft = menus.find(
    (m) => !m.archived && m.id !== main.id && !m.publishedVersionId && m.productCount > 0,
  );
  if (otherDraft) {
    return {
      title: "Ai o ciornă nepublicată",
      body: `„${otherDraft.name}” (${otherDraft.productCount} produse) nu este publicat. Meniul public rămâne „${main.name}”.`,
      action: "Deschide ciorna",
      to: `/app/products?menu=${otherDraft.id}`,
      tone: "info",
    };
  }
  return {
    title: "Meniul este publicat",
    body: "Toate modificările sunt publicate. Clienții văd meniul la linkul și codul QR de mai jos.",
    action: "Deschide meniul",
    to: publicUrl,
    external: true,
    tone: "success",
  };
}
